//src/lib/apiClient.ts
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL?.trim() || "https://api.normex.com.br";

if (!API_BASE_URL) {
  console.warn("VITE_API_BASE_URL vazio, usando fallback");
}

const ACCESS_KEY = "normex_access_token";
const REFRESH_KEY = "normex_refresh_token";
const TENANT_SLUG_KEY = "normex_tenant_slug";
const EXPIRES_AT_KEY = "normex_expires_at";
const TENANT_ID_KEY = "normex_tenant_id";
const UNIT_ID_KEY = "normex_unit_id";

export type TokenResponse = {
  token_type: "bearer" | string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  tenant_id: string;
  tenant_slug: string;
  unit_id: string;
};

export type GenericMessageResponse = {
  message: string;
};

type ApiErrorPayload = { detail?: string; message?: string } | string | null;
type QueryValue = string | number | boolean | null | undefined;
type QueryParams = Record<string, QueryValue | QueryValue[]>;

type ResponseType = "json" | "blob" | "text";

type RequestConfig = {
  auth?: boolean;
  params?: QueryParams;
  responseType?: ResponseType;
};

export type DownloadResponse = {
  blob: Blob;
  filename?: string;
};

let accessToken: string | null = localStorage.getItem(ACCESS_KEY);
let refreshToken: string | null = localStorage.getItem(REFRESH_KEY);

export function getAccessToken() {
  return accessToken;
}

export function getRefreshToken() {
  return refreshToken;
}

export function getTenantSlug(): string {
  try {
    const fromStorage =
      localStorage.getItem("tenant_slug") || localStorage.getItem("tenantSlug");
    if (fromStorage && fromStorage.trim()) return fromStorage.trim();

    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("tenant");
    if (fromQuery && fromQuery.trim()) return fromQuery.trim();

    const host = window.location.hostname;
    const parts = host.split(".");
    if (parts.length >= 3) {
      const maybeTenant = parts[0];
      if (maybeTenant && maybeTenant !== "www") return maybeTenant;
    }

    return "";
  } catch {
    return "";
  }
}

export function setTenantSlug(tenant: string) {
  try {
    localStorage.setItem("tenant_slug", tenant);
  } catch {
    // ignore
  }
}

export function getTenantId() {
  return localStorage.getItem(TENANT_ID_KEY);
}

export function getUnitId() {
  return localStorage.getItem(UNIT_ID_KEY);
}

export function getExpiresAt() {
  const v = localStorage.getItem(EXPIRES_AT_KEY);
  return v ? Number(v) : null;
}

function setSessionFromTokenResponse(data: TokenResponse) {
  accessToken = data.access_token;
  refreshToken = data.refresh_token;

  localStorage.setItem(ACCESS_KEY, data.access_token);
  localStorage.setItem(REFRESH_KEY, data.refresh_token);
  localStorage.setItem(TENANT_SLUG_KEY, data.tenant_slug);
  localStorage.setItem(TENANT_ID_KEY, data.tenant_id);
  localStorage.setItem(UNIT_ID_KEY, data.unit_id);

  try {
    localStorage.setItem("tenant_slug", data.tenant_slug);
  } catch {
    // ignore
  }

  const expiresAt = Date.now() + data.expires_in * 1000;
  localStorage.setItem(EXPIRES_AT_KEY, String(expiresAt));
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;

  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(TENANT_SLUG_KEY);
  localStorage.removeItem(EXPIRES_AT_KEY);
  localStorage.removeItem(TENANT_ID_KEY);
  localStorage.removeItem(UNIT_ID_KEY);

  localStorage.removeItem("tenant_slug");
  localStorage.removeItem("tenantSlug");
}

async function safeReadJson(res: Response): Promise<ApiErrorPayload> {
  const text = await res.text().catch(() => "");
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractErrorMessage(payload: ApiErrorPayload, fallback: string) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  return payload.detail || payload.message || fallback;
}



function getFilenameFromContentDisposition(value: string | null): string | undefined {
  if (!value) return undefined;

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const asciiMatch = value.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1];
}

function buildUrl(endpoint: string, params?: QueryParams) {
  const url = new URL(`${API_BASE_URL}${endpoint}`);

  if (!params) return url.toString();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== "") {
          url.searchParams.append(key, String(item));
        }
      });
      return;
    }

    url.searchParams.set(key, String(value));
  });

  return url.toString();
}

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  if (!refreshToken) return false;
  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!res.ok) return false;

      const data = (await res.json().catch(() => null)) as TokenResponse | null;
      if (!data?.access_token || !data?.refresh_token) return false;

      setSessionFromTokenResponse(data);
      return true;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestInit = {},
  config: RequestConfig = {},
): Promise<T> {
  const auth = config.auth ?? true;
  const responseType = config.responseType ?? "json";
  const url = buildUrl(endpoint, config.params);

  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };

  if (
    options.body !== undefined &&
    !headers["Content-Type"] &&
    !(typeof FormData !== "undefined" && options.body instanceof FormData)
  ) {
    headers["Content-Type"] = "application/json";
  }

  if (auth && accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  let response = await fetch(url, {
    ...options,
    headers,
  });

  if (auth && response.status === 401 && refreshToken) {
    const refreshed = await tryRefreshToken();

    if (refreshed && accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
      response = await fetch(url, {
        ...options,
        headers,
      });
    } else {
      clearTokens();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      throw new Error("Sessão expirada");
    }
  }

  if (!response.ok) {
    const payload = await safeReadJson(response);
    throw new Error(extractErrorMessage(payload, `Erro ${response.status}`));
  }

  if (response.status === 204) return undefined as T;

  if (responseType === "blob") {
    const blob = await response.blob();
    const filename = getFilenameFromContentDisposition(response.headers.get("content-disposition"));
    return { blob, filename } as T;
  }

  const text = await response.text().catch(() => "");
  if (!text) return undefined as T;

  if (responseType === "text") {
    return text as unknown as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

export const api = {
  get: <T = unknown>(endpoint: string, config?: RequestConfig) =>
    apiRequest<T>(endpoint, { method: "GET" }, config),

  post: <T = unknown>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig,
  ) =>
    apiRequest<T>(
      endpoint,
      {
        method: "POST",
        body: body !== undefined ? JSON.stringify(body) : undefined,
      },
      config,
    ),

  put: <T = unknown>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig,
  ) =>
    apiRequest<T>(
      endpoint,
      {
        method: "PUT",
        body: body !== undefined ? JSON.stringify(body) : undefined,
      },
      config,
    ),

  patch: <T = unknown>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig,
  ) =>
    apiRequest<T>(
      endpoint,
      {
        method: "PATCH",
        body: body !== undefined ? JSON.stringify(body) : undefined,
      },
      config,
    ),

  delete: <T = unknown>(endpoint: string, config?: RequestConfig) =>
    apiRequest<T>(endpoint, { method: "DELETE" }, config),

  download: (endpoint: string, config?: RequestConfig) =>
    apiRequest<DownloadResponse>(endpoint, { method: "GET" }, {
      ...(config || {}),
      responseType: "blob",
    }),
};

export async function authToken(payload: { email: string; password: string }) {
  const data = await api.post<TokenResponse>("/auth/token", payload, {
    auth: false,
  });
  setSessionFromTokenResponse(data);
  return data;
}

export async function forgotPasswordRequest(email: string) {
  return api.post<GenericMessageResponse>(
    "/auth/forgot-password",
    { email },
    { auth: false },
  );
}

export async function resetPasswordRequest(token: string, newPassword: string) {
  return api.post<GenericMessageResponse>(
    "/auth/reset-password",
    {
      token,
      new_password: newPassword,
    },
    { auth: false },
  );
}
